// @purpose TELEMETRY
// Journeys plan events (docs/design/JOURNEYS.md §4, 2026-10-13), all product-stream, no PII:
// - nav_clicked: a header, About-menu or tab-bar link (item = its visible label, group = where it sits);
// - quiz_shelf_action: a card on the My space quiz board (data-shelf-quiz / data-shelf-action);
// - thanks_viewed: the My space "Thanks to you" card scrolled into view, once per page.
// SEO/GEO audit contract (2026-10-13, content/events/events.schema.json `audit_contract`):
// - answer_viewed: a public page (it carries <meta name="calm-page" content="kind:id">, never a private
//   route) loaded, once per path; with the session's acquisition channel and the page's variant if any;
// - mechanism_opened: a <details> explanation in <main> opened on such a page, once per element.
// Delegated on document, so boosted page swaps need no re-binding.

import { track } from "../track.js";

function navGroup(el) {
  if (el.closest(".nav-menu__panel")) return "about";
  if (el.closest(".tabbar")) return "tabbar";
  return "top";
}

function label(el) {
  const strong = el.querySelector("strong");
  return ((strong || el).textContent || "").trim().slice(0, 40);
}

function watchThanks() {
  const card = document.querySelector("[data-thanks-people]");
  if (!card || card.dataset.seen || !("IntersectionObserver" in window)) return;
  const io = new IntersectionObserver((entries) => {
    if (!entries.some((e) => e.isIntersecting)) return;
    io.disconnect();
    card.dataset.seen = "1";
    track("thanks_viewed", { has_any: Number(card.dataset.thanksPeople || 0) > 0 });
  });
  io.observe(card);
}

// D122 channel kinds (apps/flows/journey.py channel_of): decided on the session's first page, then reused.
const AI = /chatgpt\.|openai\.|perplexity\.|claude\.ai|gemini\.google|copilot\.microsoft|you\.com/;
const SEARCH = /google\.|bing\.|duckduckgo\.|yahoo\.|yandex\.|ecosia\.|baidu\.|search\.brave\./;
const SOCIAL = /instagram\.|facebook\.|fb\.|t\.co|twitter\.|x\.com|whatsapp\.|wa\.me|linkedin\.|lnkd\.in|reddit\.|youtube\.|youtu\.be|threads\.|telegram\.|t\.me|pinterest\.|quora\./;

function channel() {
  try {
    const kept = sessionStorage.getItem("calm_ch");
    if (kept) return kept;
  } catch { /* storage blocked: decide per page */ }
  const q = new URLSearchParams(location.search);
  let host = "";
  try { host = document.referrer ? new URL(document.referrer).hostname : ""; } catch { host = ""; }
  if (host === location.hostname) host = "";
  let ch = "direct";
  if (q.has("ref")) ch = "shared";
  else if ([...q.keys()].some((k) => k === "c" || k.startsWith("utm_"))) ch = "campaign";
  else if (host) ch = AI.test(host) ? "ai" : SEARCH.test(host) ? "search" : SOCIAL.test(host) ? "social" : "other_site";
  try { sessionStorage.setItem("calm_ch", ch); } catch { /* fine */ }
  return ch;
}

function pageKey() {
  const m = document.querySelector('meta[name="calm-page"]');
  const [kind, id] = ((m && m.content) || "").split(":");
  return kind && id ? { page_kind: kind, page_id: id } : null;
}

let viewedPath = "";
function answerViewed() {
  const key = pageKey();
  if (!key || viewedPath === location.pathname) return;
  viewedPath = location.pathname;
  const props = { ...key, channel: channel() };
  const v = document.querySelector("main [data-variant]")?.dataset.variant;
  if (v && /^[a-z0-9_-]{1,24}$/.test(v)) props.variant = v;
  track("answer_viewed", props);
}

function mechanismOpened(e) {
  const d = e.target;
  if (!(d instanceof HTMLDetailsElement) || !d.open || d.dataset.mechSent || !d.closest("main")) return;
  const key = pageKey();
  if (!key) return;
  const raw = d.id || d.closest("[id]")?.id || (d.className || "").split(" ")[0] || "details";
  const mechanism = raw.toLowerCase().replace(/[^a-z0-9-]/g, "-").slice(0, 48);
  if (!mechanism) return;
  d.dataset.mechSent = "1";
  track("mechanism_opened", { ...key, mechanism, channel: channel() });
}

export function initJourneys() {
  document.addEventListener("click", (e) => {
    const nav = e.target.closest(".site-nav a, .nav-menu__item, .tabbar a.tabbar__link");
    if (nav) {
      track("nav_clicked", { item: label(nav), group: navGroup(nav) });
      return;
    }
    const shelf = e.target.closest("[data-shelf-quiz]");
    if (shelf) track("quiz_shelf_action", { quiz: shelf.dataset.shelfQuiz, action: shelf.dataset.shelfAction });
  });
  watchThanks();
  document.addEventListener("htmx:afterSettle", watchThanks);
  answerViewed();
  document.addEventListener("htmx:afterSettle", answerViewed); // boosted navigation: once per new path
  document.addEventListener("toggle", mechanismOpened, true); // toggle doesn't bubble: capture it
}
